import type { PendingImage } from '../store/types.ts';
import { cleanMermaidCode } from './mermaid-config.ts';

const SYSTEM_PROMPT = `You are Delegate Studio, an expert Mermaid diagram generator.
Return ONLY valid Mermaid syntax.
Do not include markdown fences or explanation.
Output plain Mermaid code only.
When modifying existing diagrams, preserve user intent and structure while improving correctness and clarity.`;

const EXCALIDRAW_SYSTEM_PROMPT = `You are Delegate Studio, an expert diagram generator that outputs Excalidraw element skeletons.
Diagrams should ARGUE visually, not just display — the visual structure itself should communicate relationships, causality, and flow.

## Output Format
Return a JSON array of Excalidraw element skeletons wrapped in a single \`\`\`json code fence.
Do NOT include any explanation, commentary, or text outside the JSON fence.

## Global Defaults (apply to ALL elements unless overridden)
- roughness: 0 (clean modern lines)
- opacity: 100 (never use transparency for hierarchy — use color/size/stroke instead)
- fontFamily: 3 (monospace, consistent across all text)
- strokeWidth: 2

## Supported Element Types

### Containers (rectangle, ellipse, diamond)
\`\`\`json
{
  "type": "rectangle",
  "id": "api-gateway",
  "x": 100, "y": 100,
  "width": 200, "height": 80,
  "backgroundColor": "#a5d8ff",
  "strokeColor": "#1971c2",
  "strokeWidth": 2,
  "roughness": 0,
  "roundness": { "type": 3 },
  "label": { "text": "API Gateway", "fontSize": 16, "fontFamily": 3 }
}
\`\`\`
Use "ellipse" for abstract concepts/states, "diamond" for decisions/conditions, "rectangle" for services/processes.

### Text (standalone labels, titles, annotations)
\`\`\`json
{
  "type": "text",
  "id": "diagram-title",
  "x": 100, "y": 30,
  "text": "Microservices Architecture",
  "fontSize": 28,
  "fontFamily": 3,
  "strokeColor": "#1e40af"
}
\`\`\`
Use fontSize 28 for titles, 20 for subtitles, 16 for body/annotations.

### Arrows (directed connections between containers)
\`\`\`json
{
  "type": "arrow",
  "id": "gw-to-auth",
  "x": 300, "y": 140,
  "width": 150, "height": 0,
  "strokeColor": "#1971c2",
  "strokeWidth": 2,
  "roughness": 0,
  "start": { "id": "api-gateway", "type": "rectangle" },
  "end": { "id": "auth-service", "type": "rectangle" },
  "label": { "text": "JWT verify", "fontSize": 14, "fontFamily": 3 }
}
\`\`\`

### Lines (structural connectors, non-directed)
\`\`\`json
{
  "type": "line",
  "id": "section-divider",
  "x": 50, "y": 300,
  "width": 600, "height": 0,
  "strokeColor": "#64748b",
  "strokeWidth": 1,
  "roughness": 0
}
\`\`\`

## Semantic Color Palette

### Shape Colors (fill + stroke pairs)
| Purpose | backgroundColor | strokeColor |
|---------|----------------|-------------|
| Primary/Service | #a5d8ff | #1971c2 |
| Secondary | #93c5fd | #1e3a5f |
| Success/End | #b2f2bb | #2f9e44 |
| Start/Trigger | #fed7aa | #c2410c |
| Warning/Reset | #ffc9c9 | #c92a2a |
| Decision/Branch | #ffec99 | #b45309 |
| AI/LLM | #d0bfff | #6d28d9 |
| Database/Storage | #eaddfb | #7048e8 |
| Neutral/Infra | #e9ecef | #495057 |
| Error | #fecaca | #b91c1c |

### Text Colors
- Title: "#1e40af"
- Subtitle: "#3b82f6"
- Body/Detail: "#64748b"
- On light fills: "#374151"

### Arrow/Line Colors
- Match source element's stroke color for arrows
- Use "#64748b" for structural/grouping lines

## Visual Patterns (choose based on concept)
| Pattern | When to use |
|---------|-------------|
| Fan-out (radial) | One source → multiple outputs |
| Convergence (funnel) | Multiple inputs → single output |
| Timeline (horizontal line + nodes) | Sequential steps/events |
| Tree (vertical branching) | Hierarchies |
| Side-by-side columns | Comparisons |
| Cycle (circular arrows) | Iterative/feedback loops |

## Layout Rules
1. Flow top-to-bottom or left-to-right. Use consistent spacing: 180px horizontal, 140px vertical between nodes.
2. Keep containers at least 180px wide and 80px tall for readability.
3. Align nodes on a 20px grid. Snap x/y to multiples of 20.
4. Use arrows (not lines) for directed connections. Use lines for structural grouping only.
5. Place the diagram title as a standalone text element at the top-left.
6. Every element MUST have a unique descriptive "id" string (e.g., "api-gateway", "auth-service", "db-main").
7. Group related nodes visually with consistent spacing. Leave 60px+ padding around groups.
8. Default to minimal containers — use free-floating text when a box doesn't add meaning.

## Arrow Binding Rules
- Set arrow x,y near the source element's edge.
- Set width to the horizontal distance and height to the vertical distance toward the target.
- ALWAYS bind arrows: use start.id + start.type and end.id + end.type matching the container IDs and types.
- Arrow labels should be concise (1-3 words). Use fontSize 14.

## Modification Rules
When given existing elements, preserve their IDs and positions unless the user asks to restructure. Add new elements with new IDs. Remove elements only if explicitly asked.`;

function parseExcalidrawJson(raw: string): unknown[] {
  // Strip markdown JSON fences
  const fenceMatch = raw.match(/```json\s*([\s\S]*?)```/);
  const jsonStr = fenceMatch ? fenceMatch[1].trim() : raw.trim();
  const parsed = JSON.parse(jsonStr);
  if (!Array.isArray(parsed)) throw new Error('Expected a JSON array of Excalidraw elements.');
  return parsed;
}

export async function generateExcalidrawWithClaude(args: {
  apiKey: string;
  model: string;
  prompt: string;
  currentElements: unknown[] | null;
  image: PendingImage | null;
  signal: AbortSignal;
}): Promise<unknown[]> {
  const content: Array<
    | { type: 'text'; text: string }
    | { type: 'image'; source: { type: 'base64'; media_type: string; data: string } }
  > = [];

  if (args.image?.base64) {
    content.push({
      type: 'image',
      source: { type: 'base64', media_type: args.image.mediaType, data: args.image.base64 },
    });
  }

  const composedPrompt = [
    args.prompt,
    args.currentElements?.length
      ? `Current Excalidraw elements (modify these unless user asks for a brand-new diagram):\n\`\`\`json\n${JSON.stringify(args.currentElements, null, 2)}\n\`\`\``
      : '',
  ]
    .filter(Boolean)
    .join('\n\n');

  content.push({ type: 'text', text: composedPrompt });

  const response = await fetch('https://api.anthropic.com/v1/messages', {
    method: 'POST',
    signal: args.signal,
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': args.apiKey,
      'anthropic-version': '2023-06-01',
      'anthropic-dangerous-direct-browser-access': 'true',
    },
    body: JSON.stringify({
      model: args.model,
      system: EXCALIDRAW_SYSTEM_PROMPT,
      max_tokens: 10000,
      messages: [{ role: 'user', content }],
    }),
  });

  const payloadText = await response.text();

  let payload: Record<string, unknown> = {};
  try {
    payload = JSON.parse(payloadText) as Record<string, unknown>;
  } catch {
    throw new Error(`Unexpected API response (${response.status}).`);
  }

  if (!response.ok) {
    const errorObj = payload.error as { message?: string } | undefined;
    throw new Error(errorObj?.message || `Anthropic API error (${response.status}).`);
  }

  const blocks = (payload.content as Array<{ type: string; text?: string }> | undefined) || [];
  const raw = blocks
    .filter((block) => block.type === 'text' && block.text)
    .map((block) => block.text)
    .join('\n')
    .trim();

  if (!raw) throw new Error('Claude returned an empty Excalidraw response.');
  return parseExcalidrawJson(raw);
}

export async function readImageFile(file: File): Promise<PendingImage> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = String(reader.result || '');
      const base64 = dataUrl.split(',')[1] || '';
      resolve({ name: file.name, mediaType: file.type || 'image/png', base64, dataUrl });
    };
    reader.onerror = () => reject(new Error('Failed to read image file.'));
    reader.readAsDataURL(file);
  });
}

export async function generateWithClaude(args: {
  apiKey: string;
  model: string;
  prompt: string;
  currentCode: string;
  image: PendingImage | null;
  signal: AbortSignal;
}): Promise<string> {
  const content: Array<
    | { type: 'text'; text: string }
    | { type: 'image'; source: { type: 'base64'; media_type: string; data: string } }
  > = [];

  if (args.image?.base64) {
    content.push({
      type: 'image',
      source: { type: 'base64', media_type: args.image.mediaType, data: args.image.base64 },
    });
  }

  const composedPrompt = [
    args.prompt,
    args.currentCode.trim()
      ? `Current Mermaid code (modify this unless user asks for a brand-new diagram):\n${args.currentCode}`
      : '',
  ]
    .filter(Boolean)
    .join('\n\n');

  content.push({ type: 'text', text: composedPrompt });

  const response = await fetch('https://api.anthropic.com/v1/messages', {
    method: 'POST',
    signal: args.signal,
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': args.apiKey,
      'anthropic-version': '2023-06-01',
      'anthropic-dangerous-direct-browser-access': 'true',
    },
    body: JSON.stringify({
      model: args.model,
      system: SYSTEM_PROMPT,
      max_tokens: 10000,
      messages: [{ role: 'user', content }],
    }),
  });

  const payloadText = await response.text();

  let payload: Record<string, unknown> = {};
  try {
    payload = JSON.parse(payloadText) as Record<string, unknown>;
  } catch {
    throw new Error(`Unexpected API response (${response.status}).`);
  }

  if (!response.ok) {
    const errorObj = payload.error as { message?: string } | undefined;
    throw new Error(errorObj?.message || `Anthropic API error (${response.status}).`);
  }

  const blocks = (payload.content as Array<{ type: string; text?: string }> | undefined) || [];
  const code = blocks
    .filter((block) => block.type === 'text' && block.text)
    .map((block) => block.text)
    .join('\n')
    .trim();

  if (!code) throw new Error('Claude returned an empty Mermaid response.');
  return cleanMermaidCode(code);
}
